// Port of webapp/src/components/cardDetail/cardDetailProperties.tsx

import {ReactElement} from 'react'
import {Board, IPropertyTemplate} from '../../types/board'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import * as mutator from '../../state/mutator'
import {Utils, IDType} from '../../common/utils'
import {Icon, propertyIcons} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import {PropertyEditor} from './propertyEditors'
import {addablePropertyTypes} from '../../state/properties'
import {useUsers} from '../../store/users'

type Props = {
    board: Board
    card: Card
    readOnly: boolean
}

export default function CardDetailProperties({board, card, readOnly}: Props): ReactElement {
    const usersById = useUsers((state) => state.users)

    return (
        <div className='CardDetailProperties w-full'>
            {board.cardProperties.map((template) => (
                <PropertyRow
                    key={template.id}
                    board={board}
                    card={card}
                    template={template}
                    usersById={usersById}
                    readOnly={readOnly}
                />
            ))}

            {!readOnly && (
                <AddPropertyRow board={board}/>
            )}
        </div>
    )
}

function AddPropertyRow({board}: {board: Board}): ReactElement {
    const add = (type: IPropertyTemplate['type'], label: string): void => {
        void mutator.addCardProperty(board, {
            id: Utils.createGuid(IDType.BlockID),
            name: label,
            type,
            options: type === 'select' || type === 'multiSelect' ? [{
                id: Utils.createGuid(IDType.BlockID),
                value: '选项 1',
                color: 'propColorBlue',
            }] : [],
        })
    }

    return (
        <div className='flex items-center gap-3 py-1.5 pl-[196px]'>
            <ButtonWithMenu
                button={
                    <span className='flex cursor-pointer items-center gap-2 text-sm opacity-60 hover:opacity-100'>
                        <Icon name='add' size={14}/>
                        + 添加属性
                    </span>
                }
            >
                <Menu ariaLabel='添加属性'>
                    {addablePropertyTypes.map((item) => (
                        <MenuItem key={item.label + item.type} onClick={() => add(item.type, item.label)}>
                            {item.label}
                        </MenuItem>
                    ))}
                </Menu>
            </ButtonWithMenu>
        </div>
    )
}

function PropertyRow({board, card, template, usersById, readOnly}: {
    board: Board
    card: Card
    template: IPropertyTemplate
    usersById: Record<string, IUser>
    readOnly: boolean
}): ReactElement {
    const value = card.fields.properties[template.id]
    const isEmpty = value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

    return (
        <div className='group flex items-start gap-3 py-1.5'>
            <div className='flex w-[180px] shrink-0 items-center gap-2 pt-1 text-sm opacity-70'>
                <Icon name={propertyIcons[template.type] || 'help'} size={14} className='shrink-0'/>
                <span className='truncate' title={template.name}>{template.name}</span>
                {!readOnly && (
                    <ButtonWithMenu
                        className='opacity-0 group-hover:opacity-100'
                        button={<span className='IconButton h-5 w-5' aria-label='属性操作'><Icon name='dots' size={13}/></span>}
                    >
                        <Menu ariaLabel='属性选项'>
                            <MenuItem
                                icon='edit'
                                onClick={() => {
                                    const name = window.prompt('重命名属性', template.name)
                                    if (name && name !== template.name) {
                                        void mutator.updateCardProperty(board, {...template, name})
                                    }
                                }}
                            >
                                重命名属性
                            </MenuItem>
                            {template.type === 'select' || template.type === 'multiSelect' ? (
                                <>
                                    <MenuItem
                                        icon='add'
                                        onClick={() => {
                                            const value2 = window.prompt('新选项名称', '选项')
                                            if (!value2) {
                                                return
                                            }
                                            void mutator.insertPropertyOption(board, template, {
                                                id: Utils.createGuid(IDType.BlockID),
                                                value: value2,
                                                color: 'propColorDefault',
                                            })
                                        }}
                                    >
                                        添加选项
                                    </MenuItem>
                                    <MenuSeparator/>
                                    {template.options.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            icon='trash'
                                            danger={true}
                                            onClick={() => void mutator.deletePropertyOption(board, template, option)}
                                        >
                                            删除「{option.value}」
                                        </MenuItem>
                                    ))}
                                    <MenuSeparator/>
                                </>
                            ) : null}
                            <MenuItem
                                icon='trash'
                                danger={true}
                                onClick={() => void mutator.deleteCardProperty(board, template)}
                            >
                                删除属性
                            </MenuItem>
                        </Menu>
                    </ButtonWithMenu>
                )}
            </div>

            <div className='min-w-0 flex-1'>
                {template.type === 'file' ? (
                    <span className='text-sm opacity-50'>本复刻未实现附件上传。</span>
                ) : (
                    <PropertyEditor
                        template={template}
                        card={card}
                        value={value}
                        usersById={usersById}
                        readOnly={readOnly}
                        placeholder={isEmpty ? template.name : ''}
                        onChange={(next) => void mutator.changeCardFieldValue(board.id, card, template.id, next)}
                    />
                )}
            </div>
        </div>
    )
}
